/*  Shared  */
import { Notify, Response } from 'application/Shared/Helpers';
import { debug } from 'application/Shared/Utils';

/*  NPM  */
import axios from 'axios';

/*  React  */
import { useCallback, useEffect, useState } from 'react';

export const useFetchPublishers = () => {
    const [publishers, setPublishers] = useState([]);
    const [loading, setLoading] = useState(true);
    // calling refetchData will force a rerender on the hook
    const [shouldRefetchData, setShouldRefetch] = useState({});

    const refetchData = () => setShouldRefetch({});

    useEffect(() => {
        const { CancelToken } = axios;
        const source = CancelToken.source();

        const fetchData = async () => {
            setLoading(true);
            try {
                const response = await axios.get('/publisher', {
                    cancelToken: source.token,
                    params: { size: -1 }
                });
                if (!Response.hasData(response)) {
                    debug.w('No publishers found.', response);
                } else {
                    const data = Response.getData(response);

                    const sortedData = data.sort((aName, bName) =>
                        `${aName}`.localeCompare(bName)
                    );
                    setPublishers(sortedData);
                }
            } catch (error) {
                debug.e('Error getting publishers.', error);
            }
            setLoading(false);
        };
        fetchData();

        return () => {
            // Cancel the request when the component unmounts
            source.cancel();
        };
    }, [shouldRefetchData]);

    return {
        loading,
        publishers,
        refetchData
    };
};

export const usePutPublisher = (t) => {
    const [saving, setSaving] = useState(false);

    const putPublisher = useCallback(
        async ({ id, name, description }) => {
            try {
                setSaving(true);

                await axios.put(`/publisher/${id}`, { description, name });
                Notify.success(t('GlobalAdmin.Publisher.publisherUpdated'));
                setSaving(false);
                return true; // success = true
            } catch (err) {
                debug.e('Unable to update publisher.', err);
                Notify.error(t('GlobalAdmin.Publisher.publisherNotUpdated'));
                setSaving(false);
                return false; // success = false
            }
        },
        [t]
    );

    return { putPublisher, saving };
};

export const usePostPublisher = (t) => {
    const [saving, setSaving] = useState(false);

    const postPublisher = useCallback(
        async ({ name, description }) => {
            try {
                setSaving(true);
                const response = await axios.post('/publisher', {
                    description,
                    name
                });
                if (!Response.hasData(response)) {
                    debug.e('No Publisher Response', response);
                    return false;
                }

                const publisherId = Response.getId(response);

                if (!publisherId) {
                    Notify.e(t('GlobalAdmin.Publisher.publisherNotAdded'));
                    return false;
                }

                Notify.success(t('GlobalAdmin.Publisher.publisherAdded'));
                setSaving(false);
                return publisherId;
            } catch (err) {
                debug.e('Unable to add publisher', err);
                Notify.error(t('GlobalAdmin.Publisher.publisherNotAdded'));
                setSaving(false);
                return false; // success = false
            }
        },
        [t]
    );

    return { postPublisher, saving };
};

export const useDeletePublisher = (t) => {
    const [saving, setSaving] = useState(false);

    const deletePublisher = useCallback(
        async ({ id }) => {
            try {
                setSaving(true);
                const response = await axios.delete(`/publisher/${id}`);
                if (!Response.hasData(response, { noId: true })) {
                    debug.e('No Publisher Response', response);
                    return false;
                }

                Notify.success(t('GlobalAdmin.Publisher.publisherDeleted'));
                setSaving(false);
                return true; // success = true
            } catch (err) {
                debug.e('Unable to delete publisher.', err, id);
                Notify.error(t('GlobalAdmin.Publisher.publisherNotDeleted'));
                setSaving(false);
                return false; // success = false
            }
        },
        [t]
    );

    return { deletePublisher, saving };
};
